import { useEffect, useRef, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MenuSurface } from '@/components/ui/menu-surface'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import type { RecurringActions } from './useRecurringActions'

export function RowMenu({
  name,
  actions,
  cancelled,
  onOpen,
}: {
  name: string
  actions: RecurringActions
  cancelled: boolean
  onOpen: () => void
}) {
  const isReadOnly = useIsReadOnly()
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  const entries: { label: string; onSelect: () => void; danger?: boolean }[] = [
    { label: 'Open', onSelect: onOpen },
  ]
  if (!isReadOnly && cancelled) entries.push({ label: 'Restore', onSelect: actions.restore })
  if (!isReadOnly && !cancelled) {
    entries.push(
      { label: 'Edit', onSelect: actions.edit },
      { label: 'Change next due date', onSelect: actions.openReschedule },
      { label: 'Mark missed…', onSelect: actions.openMissed }
    )
    if (actions.undoLast && actions.undoLabel) {
      entries.push({ label: actions.undoLabel, onSelect: actions.undoLast })
    }
    entries.push({ label: 'Cancel reminder', onSelect: actions.askCancel, danger: true })
  }

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="icon-sm"
        aria-label={`Actions for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-[var(--fl-text-3)]"
      >
        <MoreHorizontal size={16} />
      </Button>
      {open && (
        <MenuSurface
          ref={panelRef}
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-56 py-1"
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return
            e.stopPropagation()
            setOpen(false)
          }}
        >
          {entries.map((entry) => (
            <button
              key={entry.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                entry.onSelect()
              }}
              className={
                'flex min-h-10 w-full items-center px-3 py-1.5 text-left text-sm hover:bg-[var(--fl-surface-hover)] ' +
                (entry.danger
                  ? 'border-t border-[var(--fl-divider)] text-[var(--fl-error)]'
                  : 'text-[var(--fl-text-1)]')
              }
            >
              {entry.label}
            </button>
          ))}
        </MenuSurface>
      )}
    </div>
  )
}
